import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Pagination from 'components/pagination/Pagination';
import {
    getPaginatedDevis,
    sendDevisEmail,
    acceptDevis,
    refuseDevis,
} from 'interfaces/devis';
import { getInvoiceLineTotalHT, toNumber } from 'views/orders/createOrders/utils';
import LoadingTableLine from './loadingTableLine';

const STATUS_LABELS: Record<string, string> = {
    draft: 'Brouillon',
    sent: 'Envoyé',
    accepted: 'Accepté',
    refused: 'Refusé',
};

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-200 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-200 text-green-700',
    refused: 'bg-red-100 text-red-700',
};

const TABS: { value: string; label: string }[] = [
    { value: 'all', label: 'Tous' },
    { value: 'draft', label: 'Brouillons' },
    { value: 'sent', label: 'Envoyés' },
    { value: 'accepted', label: 'Acceptés' },
    { value: 'refused', label: 'Refusés' },
];

const DevisTable = (props: { search: string; range?: any }) => {
    const { search, range } = props;
    const nav = useNavigate();
    const [devis, setDevis] = useState<any>(null);
    const [meta, setMeta] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [limitPerPage, setLimitPerpage] = useState(10);
    const [status, setStatus] = useState('all');
    const [pendingActionId, setPendingActionId] = useState<string | null>(null);

    const fetchDevis = async (
        pageParam: number = page,
        limitParam: number = limitPerPage,
        statusParam: string = status,
        searchParam: string = search,
    ) => {
        setDevis(null);
        const res = await getPaginatedDevis(pageParam, limitParam, statusParam, searchParam, range);
        if (res) {
            setDevis(res.data);
            setMeta(res.meta);
            setPage(pageParam);
            setLimitPerpage(limitParam);
        }
    };

    useEffect(() => {
        fetchDevis(1, limitPerPage, status, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchDevis(1, limitPerPage, status, search);
        }, 600);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const formatDate = (date: any) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR');
    };

    const getDevisMarginSummary = (devis: any) => {
        const items = Array.isArray(devis?.items) ? devis.items : [];
        let totalMargin = 0;
        let includedSellHT = 0;
        let excludedCount = 0;

        items.forEach((item: any) => {
            const purchasePrice = toNumber(
                item?.totalPurchasePrice ?? item?.purchasePrice,
            );
            const quantity = toNumber(item?.quantity);
            const hasPurchasePrice =
                (item?.totalPurchasePrice !== undefined &&
                    item?.totalPurchasePrice !== null &&
                    item?.totalPurchasePrice !== '') ||
                (item?.purchasePrice !== undefined &&
                    item?.purchasePrice !== null &&
                    item?.purchasePrice !== '');

            if (!hasPurchasePrice || quantity <= 0) {
                excludedCount += 1;
                return;
            }

            const lineHT = getInvoiceLineTotalHT([item]);
            includedSellHT += lineHT;
            totalMargin += lineHT - purchasePrice * quantity;
        });

        const marginRate =
            includedSellHT > 0 ? (totalMargin / includedSellHT) * 100 : null;

        return {
            totalMargin,
            marginRate,
            excludedCount,
            hasComputedMargin: includedSellHT > 0,
        };
    };

    const handleSend = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setPendingActionId(id);
        const res = await sendDevisEmail(id);
        setPendingActionId(null);
        if (!res?.error) {
            toast.success('Devis envoyé au client');
            fetchDevis();
        }
    };

    const handleAccept = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setPendingActionId(id);
        const res = await acceptDevis(id);
        setPendingActionId(null);
        if (!res?.error) {
            toast.success('Devis accepté, commande générée');
            fetchDevis();
        }
    };

    const handleRefuse = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setPendingActionId(id);
        const res = await refuseDevis(id);
        setPendingActionId(null);
        if (!res?.error) {
            toast.success('Devis marqué comme refusé');
            fetchDevis();
        }
    };

    return (
        <div className="mt-2">
            <div className="flex flex-wrap gap-2 sm:gap-4 px-2 sm:px-0 mb-3">
                {TABS.map((tab) => (
                    <p
                        key={tab.value}
                        className={`text-[11px] sm:text-xs ${
                            status === tab.value
                                ? 'text-toola-500 underline font-medium'
                                : 'text-toola-500/80'
                        } hover:underline hover:cursor-pointer whitespace-nowrap`}
                        onClick={() => setStatus(tab.value)}
                    >
                        {tab.label}
                        {meta && tab.value !== 'all' && ` (${meta[tab.value] ?? 0})`}
                    </p>
                ))}
            </div>

            {devis && devis.length === 0 && (
                <div className="col-span-12 mt-10 text-center">
                    <h4 className="text-xl font-medium text-navy-700 dark:text-white">
                        Aucun devis
                    </h4>
                    <p className="mt-2 text-sm text-gray-600">
                        Créez un devis pour l'envoyer à un client.
                    </p>
                </div>
            )}

            {!devis && (
                <div className="flex flex-col gap-3">
                    {Array(5)
                        .fill(0)
                        .map((_, i) => (
                            <LoadingTableLine key={i} />
                        ))}
                </div>
            )}

            {devis && devis.length > 0 && (
                <div className="rounded-lg border border-r">
                    <div className="relative overflow-x-auto">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                            <thead className="text-gray-700 bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-2 py-3">
                                        # Devis
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Client
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Statut
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Date de validité
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Marge Brute
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Montant HT
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Montant TTC
                                    </th>
                                    <th scope="col" className="px-2 py-3">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {devis.map((item: any) => {
                                    const marginSummary = getDevisMarginSummary(item);
                                    return (
                                    <tr
                                        key={item._id}
                                        className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => nav(`/devis/${item._id}`)}
                                    >
                                        <td className="px-2 py-3">#{item.devisNumber}</td>
                                        <td className="px-2 max-w-[180px] py-3">
                                            {item.displayName || item.company}
                                        </td>
                                        <td className="px-2 py-3">
                                            <span
                                                className={`text-xs px-2 py-1 rounded-full ${
                                                    STATUS_COLORS[item.status] || 'bg-gray-200 text-gray-700'
                                                }`}
                                            >
                                                {STATUS_LABELS[item.status] || item.status}
                                            </span>
                                        </td>
                                        <td className="px-2 py-3">{formatDate(item.validUntil)}</td>
                                        <td className="px-2 py-3">
                                            {marginSummary.hasComputedMargin ? (
                                                <>
                                                    <p>
                                                        {marginSummary.totalMargin.toFixed(2)}
                                                        {' €'}
                                                    </p>
                                                    {marginSummary.marginRate !== null && (
                                                        <p className="text-xs text-gray-700">
                                                            {parseFloat(marginSummary.marginRate.toFixed(2))}%
                                                        </p>
                                                    )}
                                                    {marginSummary.excludedCount > 0 && (
                                                        <p className="text-xs text-orange-500">
                                                            {marginSummary.excludedCount} produit
                                                            {marginSummary.excludedCount > 1 ? 's' : ''} sans coût
                                                            exclu{marginSummary.excludedCount > 1 ? 's' : ''}
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                'N/A'
                                            )}
                                        </td>
                                        <td className="px-2 py-3">{item.totalHT} €</td>
                                        <td className="px-2 py-3">{item.totalTTC} €</td>
                                        <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex flex-wrap gap-2">
                                                {(item.status === 'draft' || item.status === 'sent') && (
                                                    <button
                                                        disabled={pendingActionId === item._id}
                                                        className="text-xs text-toola-500 hover:underline disabled:opacity-50"
                                                        onClick={(e) => handleSend(e, item._id)}
                                                    >
                                                        {item.status === 'sent' ? 'Renvoyer' : 'Envoyer'}
                                                    </button>
                                                )}
                                                {(item.status === 'draft' || item.status === 'sent') && (
                                                    <>
                                                        <button
                                                            disabled={pendingActionId === item._id}
                                                            className="text-xs text-green-600 hover:underline disabled:opacity-50"
                                                            onClick={(e) => handleAccept(e, item._id)}
                                                        >
                                                            Accepter
                                                        </button>
                                                        <button
                                                            disabled={pendingActionId === item._id}
                                                            className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                                            onClick={(e) => handleRefuse(e, item._id)}
                                                        >
                                                            Refuser
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {devis && (
                <Pagination
                    extra="mt-3"
                    text={true}
                    maxItems={meta ? meta.sectionTotal : 12}
                    itemsPerPage={[8, 12, 16, 30, 50, 100]}
                    defaultItemsPerPage={limitPerPage}
                    page={page}
                    onChange={(pageParam: number, limitParam: number) =>
                        fetchDevis(pageParam, limitParam, status, search)
                    }
                />
            )}
        </div>
    );
};

export default DevisTable;
